import React, { useState, useEffect } from 'react';
import UserProfileDialog from '../components/dashboard/UserProfileDialog';
import { TableSkeleton } from '../../components/loading/skeletons';
import { adminListAppUsers, adminUpdateAppUser, toUserDate } from '../api/appUsersApi';

const AllSignups = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [updatingReachOut, setUpdatingReachOut] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const { users: resolvedUsers } = await adminListAppUsers({
        role: 'owner',
        accountType: 'company',
        createdAfter: thirtyDaysAgo.toISOString(),
      });
      const ownersOnly = resolvedUsers.filter((user) => user.role === 'owner' && user.type !== 'employee');
      setUsers(ownersOnly);
      setFilteredUsers(ownersOnly);
      setError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(user => {
      const searchString = searchTerm.toLowerCase();
      return (
        user.firstName?.toLowerCase().includes(searchString) ||
        user.lastName?.toLowerCase().includes(searchString) ||
        user.email?.toLowerCase().includes(searchString) ||
        user.companyName?.toLowerCase().includes(searchString)
      );
    });
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (timestamp) => {
    const date = toUserDate(timestamp);
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleReachOutToggle = async (e, userId, currentStatus) => {
    e.stopPropagation();
    setUpdatingReachOut(userId);
    try {
      const updated = await adminUpdateAppUser(userId, { reachedOut: !currentStatus });
      setUsers((current) => current.map((user) => (user.id === userId ? { ...user, ...updated } : user)));
    } catch (error) {
      console.error('Error updating reach out status:', error);
    } finally {
      setUpdatingReachOut(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <TableSkeleton rows={10} columns={[200, 180, 200, 140, 100]} label="Loading signups" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      {error ? <p className="admin-alert">{error}</p> : null}

      <h1 className="admin-heading-sm">Company Signups (Last 30 Days)</h1>
      <input
        className="admin-field"
        style={{ maxWidth: 420, margin: '12px 0 16px' }}
        type="search"
        placeholder="Search by name, email, or company..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Email</th>
              <th>Package</th>
              <th>Signup Date</th>
              <th className="table-actions">Reached Out</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr className="data-table-empty-row">
                <td colSpan={6}>No signups found.</td>
              </tr>
            ) : (
              filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                <tr
                  key={user.id}
                  className="is-clickable"
                  onClick={() => setSelectedUser(user)}
                >
                  <td>{user.firstName} {user.lastName}</td>
                  <td style={{ color: 'var(--admin-primary)', fontWeight: 500 }}>
                    {user.companyName}
                  </td>
                  <td>{user.email}</td>
                  <td>{user.package?.name || 'N/A'}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td className="table-actions">
                    <button
                      type="button"
                      className={`admin-icon-btn${user.reachedOut ? ' is-done' : ''}`}
                      title={user.reachedOut
                        ? `Reached out on ${formatDate(user.reachedOutAt)} by ${user.reachedOutBy}`
                        : 'Mark as reached out'}
                      onClick={(e) => handleReachOutToggle(e, user.id, user.reachedOut)}
                      disabled={updatingReachOut === user.id}
                    >
                      {updatingReachOut === user.id ? '…' : user.reachedOut ? '✓' : '○'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="table-pager">
          <label>
            Rows
            <select
              className="admin-field"
              style={{ width: 'auto', minHeight: '2rem', marginLeft: 8 }}
              value={rowsPerPage}
              onChange={(e) => handleChangeRowsPerPage(e)}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
          <span>
            {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filteredUsers.length)} of {filteredUsers.length}
          </span>
          <button type="button" disabled={page === 0} onClick={() => handleChangePage(null, page - 1)}>
            Prev
          </button>
          <button
            type="button"
            disabled={(page + 1) * rowsPerPage >= filteredUsers.length}
            onClick={() => handleChangePage(null, page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <UserProfileDialog
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        user={selectedUser}
      />
    </div>
  );
};

export default AllSignups; 